// Cross-surface "return me where I was" context.
//
// Four separate items in the Post-Implementation Corrections delta (§1.1, §1.10,
// §2.1, §3.3) are the same requirement, so they share one mechanism rather than
// growing four ad-hoc ones.
//
// Why sessionStorage rather than router state or a query param:
//
//   - `location.state` cannot work. The buckets screens link out to a story with
//     a plain <a href>, which is a full page load, and router state does not
//     survive that.
//   - A query param cannot work either. "Item Details" is step 1 of the
//     CreateBrief wizard, and an effect in CreateBriefComponent mirrors the step
//     into the URL with `replace: true` and NO search string — so any param on
//     /story/:slug is destroyed at the first step transition.
//
// sessionStorage survives both, and is scoped to the tab, so a second tab does
// not inherit a stale return destination. This follows the existing precedent in
// features/CreateBrief/briefStatePersistence.js and pages/PublicSuggestionsPage.jsx.

// Two independent slots, because two unrelated questions were otherwise sharing
// one answer:
//
//   SCREEN — "where should Bucket Stories' back control go?"  Written when a
//            BUCKET is opened (from Publisher Buckets or In Production).
//   STORY  — "where should the story screen's Back go?"  Written when a STORY is
//            opened from Bucket Stories.
//
// With a single slot, a stale SCREEN entry left by a bucket click could be
// consumed by the story's Back and navigate somewhere the user never came from.
export const SLOTS = Object.freeze({ SCREEN: 'screen', STORY: 'story' });

const STORAGE_KEYS = Object.freeze({
  [SLOTS.SCREEN]: 'alchemiq.navReturn.screen',
  [SLOTS.STORY]: 'alchemiq.navReturn.story',
});

const keyFor = (slot) => STORAGE_KEYS[slot] || STORAGE_KEYS[SLOTS.SCREEN];

// How long a saved return context stays valid. Long enough to survive a story
// page load on a slow connection, short enough that an abandoned entry cannot
// resurface as unexplained filter state on a later visit.
const RETURN_TTL_MS = 30 * 60 * 1000;

/** Surfaces that can be returned to. */
export const SURFACES = Object.freeze({
  BUCKET_STORIES: 'bucketStories',
  IN_PRODUCTION: 'inProduction',
  PUBLISHER_BUCKETS: 'publisherBuckets',
});

/**
 * Record where the user is leaving from.
 *
 * @param {object} entry
 * @param {string} entry.surface  one of SURFACES
 * @param {string} entry.url      absolute in-app path to return to
 * @param {string} [entry.label]  button copy, e.g. "Back to In Production"
 * @param {object} [entry.state]  arbitrary restorable screen state
 * @param {string} [slot]         SLOTS.SCREEN (default) or SLOTS.STORY
 */
export function saveReturn({ surface, url, label = null, state = null }, slot = SLOTS.SCREEN) {
  if (!surface || !url || typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(
      keyFor(slot),
      JSON.stringify({ surface, url, label, state, savedAt: Date.now() }),
    );
  } catch (error) {
    // A full quota is not worth breaking navigation over — the user simply gets
    // the default Back behaviour.
    console.warn('navContext: could not save return context:', error);
  }
}

/**
 * Read the pending return context WITHOUT clearing it.
 *
 * Use this to decide whether to render a "Back to …" control. Clearing here
 * would mean the control vanishes on re-render.
 *
 * @param {string} [surface] when given, returns null unless the stored entry
 *                           matches that surface
 */
export function peekReturn(surface = null, slot = SLOTS.SCREEN) {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(keyFor(slot));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || !parsed.url) return null;
    if (surface && parsed.surface !== surface) return null;
    // §7.3 — the Back handler PEEKS rather than consumes, so the destination
    // screen can still read the payload it needs (which row was expanded, the
    // viewport anchor, filters). That leaves the entry alive if the user never
    // reaches that destination, so bound it in time: a stale entry must not
    // silently restore filters onto a plain visit made much later. The window
    // only has to cover a navigation, so it is deliberately short.
    if (parsed.savedAt && Date.now() - parsed.savedAt > RETURN_TTL_MS) {
      clearReturn(slot);
      return null;
    }
    return parsed;
  } catch (error) {
    console.warn('navContext: could not read return context:', error);
    return null;
  }
}

/** Read and clear in one step — call when actually performing the return. */
export function consumeReturn(surface = null, slot = SLOTS.SCREEN) {
  const entry = peekReturn(surface, slot);
  if (entry) clearReturn(slot);
  return entry;
}

export function clearReturn(slot = SLOTS.SCREEN) {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(keyFor(slot));
  } catch (error) {
    console.warn('navContext: could not clear return context:', error);
  }
}

/**
 * Restore a scroll position once the container exists and can hold it.
 *
 * Two separate waits, which is the point:
 *
 *   1. The ELEMENT may not exist yet. These screens render `null` until an async
 *      permission check resolves, so the scroll container can appear well after
 *      the rows data has arrived. A budget of a few animation frames expired
 *      long before that and wrote nothing at all — the bug that survived six
 *      fixes. This polls on a real time budget instead.
 *   2. Once it exists, LAYOUT may not have settled, so an assignment silently
 *      clamps. That part retries per frame until the value sticks.
 *
 * @param {() => HTMLElement|null} getEl resolved late; the ref may not be set yet
 * @param {number} top
 * @param {number} [waitMs] how long to wait for the element to appear
 * @returns {() => void} cancel
 */
export function restoreScroll(getEl, top, waitMs = 6000) {
  if (typeof window === 'undefined' || top == null || top <= 0) return () => {};
  const deadline = Date.now() + waitMs;
  let timer = 0;
  let raf = 0;
  let settleLeft = 40;
  let cancelled = false;

  const settle = () => {
    if (cancelled) return;
    const el = getEl();
    if (!el) return;
    if (Math.abs(el.scrollTop - top) <= 2) return;   // landed
    el.scrollTop = top;
    if (Math.abs(el.scrollTop - top) <= 2) return;   // landed
    // No "already at the bottom" shortcut: before the rows render
    // scrollHeight ≈ clientHeight, which makes that test trivially true and
    // reports success on the first frame.
    if (--settleLeft > 0) raf = window.requestAnimationFrame(settle);
  };

  const waitForEl = () => {
    if (cancelled) return;
    if (getEl()) { settle(); return; }
    if (Date.now() < deadline) timer = window.setTimeout(waitForEl, 50);
  };

  waitForEl();
  return () => {
    cancelled = true;
    window.clearTimeout(timer);
    window.cancelAnimationFrame(raf);
  };
}
